<script>
import { createNamespacedHelpers } from "vuex"

const { mapState, mapActions } = createNamespacedHelpers("pricing")

export default {
  name: "CPricing",
  props: {
    activePlanId: {
      type: String,
      default: "",
    },
    periodUnit: {
      type: String,
      default: "month",
    },
  },
  emits: ["fetched"],
  data() {
    return {
      isFetching: this.$store.state.pricing.pricingPlans.length === 0,
    }
  },
  computed: {
    ...mapState(["pricingPlans"]),
    normalizedPlans() {
      const options = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: "currency",
      }
      const priceFormats = {
        EUR: new Intl.NumberFormat(undefined, {
          ...options,
          currency: "EUR",
        }),
        USD: new Intl.NumberFormat(undefined, {
          ...options,
          currency: "USD",
        }),
      }

      return this.pricingPlans
        // remove all debug, beta and plans before 2022
        .filter(({ id, bundle }) => {
          // IMPORTANT! This old plan is still used because it's the original trial plan before
          // plans depending on the currency were introduced.
          if (id === "prod-28-08-2017_prtg-trial") {
            return true
          }

          // e.g. prod-01-01-2022 => ["prod", ["01", "01", "2022"]]
          const [head, ...tail] = bundle.split("-")
          // valid plans always start with prod-xx-xx-xxxx
          if (head !== "prod") {
            return false
          }

          // reverse the date to xxxx-xx-xx so js Date can convert it to Date object
          // all valid plans have an older date then 01.01.2022 in their bundle name
          return new Date(`${tail[2]}-${tail[1]}-${tail[0]}`) > new Date("2022-01-01")
        })
        .map(plan => ({
          ...plan,
          price: priceFormats[plan.currencyCode].format(plan.price / 100),
          active: plan.id === this.activePlanId,
        }))
    },
    baseMonthAnnualPlans() {
      return this.getFilteredMainPlans()
    },
    multiAnnualPlans() {
      return this.getFilteredMultiAnnualPlans()
    },
    trialPlan() {
      return this.normalizedPlans.find(
        ({ trial, currencyCode }) => trial && currencyCode === this.visitor.currencyCode,
      ) || {}
    },
    visitor() {
      return this.$store.state.visitor.visitor
    },
    currentPricingPlan() {
      return this.normalizedPlans.find(({ id }) => id === this.activePlanId) || {}
    },
    canHaveMonthlyAndAnnualPlans() {
      return this.hasPlan && this.currentPricingPlan?.periodUnit === "month"
    },
    hasPlan() {
      return Object.entries(this.currentPricingPlan).length > 0
    },
    isTrial() {
      return this.currentPricingPlan?.trial || false
    },
    is3YearMYPA() {
      return this.currentPricingPlan?.id?.includes("3_years_paid_annually") || false
    },
    is5YearMYPA() {
      return this.currentPricingPlan?.id?.includes("5_years_paid_annually") || false
    },
    is3YearRegular() {
      return this.currentPricingPlan?.period === 3 && !this.currentPricingPlan?.id?.includes("paid_annually")
    },
    is5YearRegular() {
      return this.currentPricingPlan?.period === 5 && !this.currentPricingPlan?.id?.includes("paid_annually")
    },
    isMonthly() {
      return this.currentPricingPlan?.periodUnit === "month"
    },
    is1Year() {
      return this.currentPricingPlan?.period === 1 && !this.currentPricingPlan?.id?.includes("paid_annually")
    },
  },
  async created() {
    await this.$store.dispatch("visitor/fetchVisitor")
    await this.fetchPricingPlans()
    this.isFetching = false
    this.$emit("fetched")
  },
  methods: {
    ...mapActions(["fetchPricingPlans"]),

    getFilteredMainPlans() {
      let filteredPlans = this.normalizedPlans
        .filter(({ trial }) => !trial)
        .filter(({ currencyCode }) => {
          if (this.hasPlan) {
            return currencyCode === this.currentPricingPlan.currencyCode
          }
          return currencyCode === this.visitor.currencyCode
        })

      // New subscriptions (no current plan): Only show 3-year MYPA plans
      if (!this.hasPlan) {
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }

      // Special handling for monthly users when monthly tab is selected
      if (this.periodUnit === "month" && this.isMonthly) {
        return filteredPlans.filter(plan => plan.periodUnit === "month")
      }

      // For all other cases (annual tab or non-monthly users), show annual plans
      if (this.isTrial) {
        // Trial users: Only show 3-year MYPA plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }
      else if (this.is3YearMYPA) {
        // 3-year MYPA users: Only show other 3-year MYPA plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }
      else if (this.is5YearMYPA) {
        // 5-year MYPA users: Only show other 5-year MYPA plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("5_years_paid_annually"),
        )
      }
      else if (this.is3YearRegular) {
        // 3-year regular users: Show other 3-year plans (no MYPA)
        return filteredPlans.filter(plan =>
          plan.period === 3
          && plan.periodUnit === "year",
        )
      }
      else if (this.is5YearRegular) {
        // 5-year regular users: Only show other 5-year plans (no MYPA)
        return filteredPlans.filter(plan =>
          plan.period === 5
          && plan.periodUnit === "year",
        )
      }
      else if (this.isMonthly || this.is1Year) {
        // Monthly users (annual tab) OR 1-year users: Show 1-year annual plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && !plan.id?.includes("paid_annually"),
        )
      }
      else {
        // Fallback: show regular 1-year annual plans
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && !plan.id?.includes("paid_annually"),
        )
      }
    },

    getFilteredMultiAnnualPlans() {
      let filteredPlans = this.normalizedPlans
        .filter(({ trial }) => !trial)
        .filter(({ currencyCode }) => {
          if (this.hasPlan) {
            return currencyCode === this.currentPricingPlan.currencyCode
          }
          return currencyCode === this.visitor.currencyCode
        })

      // New subscriptions (no current plan): No multi-annual options
      if (!this.hasPlan) {
        return []
      }

      // No multi-annual plans when monthly tab is selected
      if (this.periodUnit === "month") {
        return []
      }

      if (this.isTrial || this.is3YearMYPA || this.is5YearMYPA || this.is5YearRegular) {
        // MYPA users, 5-year regular users, and trial users get no multi-annual plans (simple buttons)
        return []
      }
      else if (this.is3YearRegular) {
        // 3-year regular users: Show 5-year regular plans as multi-annual options
        return filteredPlans.filter(plan =>
          plan.period === 5
          && plan.periodUnit === "year",
        )
      }
      else if (this.isMonthly || this.is1Year) {
        // Monthly/1-year users: Show 3-year MYPA as multi-annual options
        return filteredPlans.filter(plan =>
          plan.period === 1
          && plan.periodUnit === "year"
          && plan.id
          && plan.id.includes("3_years_paid_annually"),
        )
      }
      else {
        // Fallback: show all multi-annual options
        return filteredPlans.filter(({ period, periodUnit, id }) =>
          (period > 1 && periodUnit === "year")
          || (period === 1 && periodUnit === "year" && id && id.includes("paid_annually")),
        )
      }
    },
  },
  render() {
    return this.$slots.default({
      baseMonthAnnualPlans: this.baseMonthAnnualPlans,
      currentPricingPlan: this.currentPricingPlan,
      isFetching: this.isFetching,
      trialPlan: this.trialPlan,
      multiAnnualPlans: this.multiAnnualPlans,
      canHaveMonthlyAndAnnualPlans: this.canHaveMonthlyAndAnnualPlans,
    })
  },
}
</script>
